
import React from 'react';
import { SimulationConfig, ShapeType, RotationMode } from './types';
import { SectionLabel } from './SectionLabel';
import { ControlGroup } from './ControlGroup';
import { SmartInput } from './SmartInput';
import { Toggle } from './Toggle';

interface ShapeSettingsSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const ShapeSettingsSection: React.FC<ShapeSettingsSectionProps> = ({ config, setConfig }) => {
  const shapes: ShapeType[] = [
      'CIRCLE', 'SQUARE', 'TRIANGLE', 'PENTAGON', 'HEXAGON', 'HEPTAGON', 'OCTAGON', 
      'NONAGON', 'DECAGON', 'STAR_5', 'STAR_6', 'STAR_8', 'HEART', 'DIAMOND', 
      'CROSS', 'CLOVER', 'FLOWER', 'SHIELD', 'INVERTED_TRIANGLE', 'GEM', 'GEAR'
  ];

  const toggleMorphShape = (shape: ShapeType) => {
    setConfig(prev => {
        const current = prev.morphableShapes || shapes;
        if (current.includes(shape)) {
            if (current.length <= 1) return prev; 
            return { ...prev, morphableShapes: current.filter(s => s !== shape) };
        } else {
            return { ...prev, morphableShapes: [...current, shape] };
        }
    });
  };

  return (
    <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border-2 bg-indigo-600/10 border-indigo-500/30 mb-4 shadow-lg shadow-indigo-500/5">
      <SectionLabel icon="fa-shapes" darkMode={config.darkMode}>Shape Shifter</SectionLabel>
      
      <div className="flex flex-col gap-2 p-3 bg-black/10 rounded-2xl border border-indigo-500/20">
          <span className="text-[10px] font-black uppercase text-indigo-400">Container Geometry</span>
          <ControlGroup label="Outer Shape">
            <select 
                value={config.containerShape || 'CIRCLE'} 
                onChange={e => setConfig(p => ({...p, containerShape: e.target.value as ShapeType}))}
                className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            >
                {shapes.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </ControlGroup>
          
          <SmartInput 
            label="Shape Angle" 
            min={0} max={360} 
            value={config.containerRotation || 0} 
            onChange={v => setConfig(p => ({...p, containerRotation: v}))} 
            darkMode={config.darkMode} 
          />

          <div className="flex items-center justify-between mt-2">
             <span className="text-[9px] font-black uppercase opacity-60">Auto Morphing</span>
             <Toggle active={config.shapeMorphEnabled} onClick={() => setConfig(p => ({...p, shapeMorphEnabled: !p.shapeMorphEnabled}))} color="bg-indigo-500" />
          </div>
          {config.shapeMorphEnabled && (
              <>
                  <div className="flex items-center justify-between mt-1 px-1">
                     <span className="text-[8px] font-black uppercase opacity-60">Continuous Shift</span>
                     <Toggle active={config.shapeMorphContinuous} onClick={() => setConfig(p => ({...p, shapeMorphContinuous: !p.shapeMorphContinuous}))} color="bg-indigo-400" />
                  </div>
                  
                  {!config.shapeMorphContinuous && (
                      <SmartInput label="Change Interval" min={0.1} max={5.0} step={0.1} value={config.shapeMorphSpeed} onChange={v => setConfig(p => ({...p, shapeMorphSpeed: v}))} darkMode={config.darkMode} />
                  )}
                  <SmartInput label="Morph Duration (ms)" min={100} max={3000} step={100} value={config.shapeMorphDuration} onChange={v => setConfig(p => ({...p, shapeMorphDuration: v}))} darkMode={config.darkMode} />

                  <div className="mt-3 p-2 bg-black/20 rounded-xl">
                      <span className="text-[8px] font-black uppercase opacity-60 block mb-2">Morph Cycle Pool</span>
                      <div className="grid grid-cols-4 gap-1">
                          {shapes.map(s => {
                              const isActive = (config.morphableShapes || shapes).includes(s);
                              return (
                                  <button 
                                    key={s} 
                                    onClick={() => toggleMorphShape(s)}
                                    className={`text-[7px] font-bold py-1 px-1 rounded transition-all ${isActive ? 'bg-indigo-500 text-white' : 'bg-white/5 text-neutral-500 hover:bg-white/10'}`}
                                  >
                                      {s.replace('_', ' ')}
                                  </button>
                              );
                          })}
                      </div>
                  </div>
              </>
          )}
      </div>

      <div className="flex flex-col gap-2 p-3 bg-black/10 rounded-2xl border border-pink-500/20 mt-2">
          <span className="text-[10px] font-black uppercase text-pink-400">Object Geometry</span>
          <ControlGroup label="Ball Shape">
            <select 
                value={config.ballShape || 'CIRCLE'} 
                onChange={e => setConfig(p => ({...p, ballShape: e.target.value as ShapeType}))}
                className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            >
                {shapes.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </ControlGroup>

          <ControlGroup label="Rotation Logic">
            <select 
                value={config.ballRotationMode || 'VELOCITY'} 
                onChange={e => setConfig(p => ({...p, ballRotationMode: e.target.value as RotationMode}))}
                className={`w-full px-3 py-2 border rounded-xl text-[10px] font-black outline-none ${config.darkMode ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-200 text-neutral-800'}`}
            >
                <option value="LOCKED">Locked (0°)</option>
                <option value="VELOCITY">Follow Velocity</option>
                <option value="SPIN">Constant Spin</option>
            </select>
          </ControlGroup>
      </div>
    </div>
  );
};
